import { getStatusLabel } from './statusMapping';

type LeadReportCandidate = {
  id?: string | number | null;
  status?: string | null;
  campaignId?: string | null;
  leadName?: string | null;
  companyName?: string | null;
  personLvl1?: { id?: string | number | null } | null;
  personLvl2?: { lvl1_id?: string | number | null; report_lead_url?: string | null } | null;
  /** Card payloads (MinimalLeadData) carry the flattened lvl2.report_lead_url. */
  reportLeadUrl?: string | null;
  rawOriginalData?: {
    lvl1_id?: string | number | null;
    person_lvl2?: { lvl1_id?: string | number | null } | null;
    personLvl2?: { lvl1_id?: string | number | null } | null;
  } | null;
};

export type CampaignReportCandidate = {
  fileId: string;
  path: string;
  leadId: string;
  leadName: string;
  companyName: string;
};

export const REPORT_READY_STATUS_LABELS = ['ENRICHED', 'OUTREACHING', 'OUTREACHED', 'ENGAGED', 'PROPOSAL', 'CONVERTED'] as const;

export const canLeadHaveReport = (status: string | null | undefined): boolean => {
  return REPORT_READY_STATUS_LABELS.includes(getStatusLabel(status) as (typeof REPORT_READY_STATUS_LABELS)[number]);
};

export const getLeadReportFileId = (lead: LeadReportCandidate): string | null => {
  if (!lead?.campaignId || !canLeadHaveReport(lead.status)) {
    return null;
  }

  const reportLeadId =
    lead.personLvl2?.lvl1_id ??
    lead.personLvl1?.id ??
    lead.rawOriginalData?.person_lvl2?.lvl1_id ??
    lead.rawOriginalData?.personLvl2?.lvl1_id ??
    lead.rawOriginalData?.lvl1_id ??
    lead.id;

  if (!reportLeadId) {
    return null;
  }

  return `${lead.campaignId}_${reportLeadId}`;
};

export const getLeadReportStoragePath = (lead: LeadReportCandidate): string | null => {
  const fileId = getLeadReportFileId(lead);
  return fileId ? `lead-reports/${fileId}` : null;
};

/**
 * Report URL recorded on the lvl2 row (`report_lead_url` column) by the
 * enrichment workflow. A non-empty value is the source of truth that the
 * PDF exists in the lead-reports bucket — no storage probe needed. Legacy
 * leads (enriched before the column) still fall back to the probe.
 */
export const getLeadReportUrl = (lead: LeadReportCandidate | null | undefined): string | null => {
  if (!lead) return null;
  const url = lead.personLvl2?.report_lead_url ?? (lead as { reportLeadUrl?: unknown }).reportLeadUrl;
  return typeof url === 'string' && url.trim().length > 0 ? url.trim() : null;
};

export const getCampaignReportCandidates = (leads: LeadReportCandidate[]): CampaignReportCandidate[] => {
  return leads
    .filter((lead) => canLeadHaveReport(lead.status))
    .map((lead) => {
      const fileId = getLeadReportFileId(lead);
      const path = getLeadReportStoragePath(lead);

      if (!fileId || !path) {
        return null;
      }

      return {
        fileId,
        path,
        leadId: String(lead.id ?? ''),
        leadName: lead.leadName?.trim() || 'Unknown Lead',
        companyName: lead.companyName?.trim() || 'Unknown Company',
      };
    })
    .filter((value): value is CampaignReportCandidate => Boolean(value));
};
